import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TopAlert, { type AlertPayload, type AlertType } from "../components/ui/TopAlert";
import { setAlertApi } from "../utils/alertBus";

/**
 * AlertContext — web port of the mobile app's AlertProvider.
 *
 * Usage:
 *   const alert = useAlert();
 *   alert.success("Saved!", "Your changes were saved.");
 *   alert.error("Login failed", err.message);
 *   alert.warning("Heads up", "Approaching seat limit.");
 *   alert.info("Tip", "Press Ctrl+K to open search.");
 *
 * Or, low-level:
 *   alert.show({ type: "success", title, message, duration });
 *
 * Pass `duration: 0` (or negative) for a sticky alert that requires manual dismiss.
 */

interface AlertOptions {
  type?: AlertType;
  title?: string;
  message?: string;
  duration?: number; // ms, default 3500. Pass 0 for sticky.
}

interface AlertApi {
  show: (options: AlertOptions) => void;
  hide: () => void;
  success: (title?: string, message?: string, duration?: number) => void;
  error: (title?: string, message?: string, duration?: number) => void;
  warning: (title?: string, message?: string, duration?: number) => void;
  info: (title?: string, message?: string, duration?: number) => void;
}

const AlertContext = createContext<AlertApi | null>(null);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alert, setAlert] = useState<AlertPayload | null>(null);
  const idRef = useRef(0);

  const show = useCallback((options: AlertOptions = {}) => {
    idRef.current += 1;
    setAlert({
      id: idRef.current,
      type: options.type || "info",
      title: options.title,
      message: options.message,
      duration: options.duration,
    });
  }, []);

  const hide = useCallback(() => setAlert(null), []);

  const value: AlertApi = useMemo(
    () => ({
      show,
      hide,
      success: (title, message, duration) => show({ type: "success", title, message, duration }),
      error: (title, message, duration) => show({ type: "error", title, message, duration }),
      warning: (title, message, duration) => show({ type: "warning", title, message, duration }),
      info: (title, message, duration) => show({ type: "info", title, message, duration }),
    }),
    [show, hide]
  );

  // Register the live API with the alertBus so non-React code (services, axios
  // interceptors, the legacy `toast.*` shim) can render alerts without the hook.
  useEffect(() => {
    setAlertApi(value);
    return () => setAlertApi(null);
  }, [value]);

  return (
    <AlertContext.Provider value={value}>
      {children}
      <TopAlert alert={alert} onDismiss={hide} />
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertApi => {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error("useAlert must be used inside an <AlertProvider>");
  }
  return ctx;
};

export type { AlertType };
